import amplitude from 'amplitude-js';
import { PAPEvent } from '@dropbox/pap-events/base/event';
import { IntegrationSource } from '@dropbox/pap-events/enums/integration_source';
import { isProduction, snakeCaseObj } from 'js/sign-components/common/ts-utils';
import { getPAPUser } from 'js/sign-components/common/pap-user-store';
import {
  CONSENT_CHANGED_EVENT,
  hasAnalyticsConsent,
  isMarketingAttributionCookieConsentGateEnabled,
  isPAPAnalyticsConsentGateEnabled,
} from 'common/utils/cookie-consent';

type AmplitudeUserId = string | number | null | undefined;
type PAPAmplitudeClient = ReturnType<typeof amplitude.getInstance>;

const AMPLITUDE_KEY = isProduction()
  ? '233ffee62e411e84d1807b55c77e9087'
  : '36f19c68dee21f264e20d9db589114df';
const API_ENDPOINT = 'www.dropbox.com/amplitude_proxy/ingest_lenient';
const AMPLITUDE_COOKIE_PREFIX = `amp_${AMPLITUDE_KEY.slice(0, 6)}`;
// Keep this list in sync with the cookies created by ui/src/attribution/attribution.js.
const MARKETING_ATTRIBUTION_COOKIES = [
  'gclid',
  'gclid_lt',
  'par',
  'par_lt',
  'referrer_url',
  'referrer_url_lt',
  'landing_page_url',
  'landing_page_url_lt',
];

let papClientInitialized = false;
let consentListenerInitialized = false;

function expireCookie(name: string): void {
  const hostnameParts = window.location.hostname.split('.');
  const domains = hostnameParts.map((_, index) =>
    hostnameParts.slice(index).join('.'),
  );

  document.cookie = `${name}=; Max-Age=0; Path=/`;
  domains.forEach((domain) => {
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}`;
  });
}

function clearPAPClientCookies(): void {
  try {
    document.cookie
      .split(';')
      .map((cookie) => cookie.trim().split('=')[0])
      .filter((cookieName) => cookieName.startsWith(AMPLITUDE_COOKIE_PREFIX))
      .forEach(expireCookie);
  } catch {
    // Cookie access may be unavailable in privacy-focused configurations.
  }
}

function clearMarketingAttributionCookies(): void {
  try {
    MARKETING_ATTRIBUTION_COOKIES.forEach(expireCookie);
  } catch {
    // Cookie access may be unavailable in privacy-focused configurations.
  }
}

function handleAnalyticsConsentChange(): void {
  if (!hasAnalyticsConsent()) {
    if (isPAPAnalyticsConsentGateEnabled()) {
      clearPAPClientCookies();
    }
    if (isMarketingAttributionCookieConsentGateEnabled()) {
      clearMarketingAttributionCookies();
    }
  }
}

export function initializeAnalyticsConsentHandling(): void {
  if (consentListenerInitialized) {
    return;
  }

  window.addEventListener(CONSENT_CHANGED_EVENT, handleAnalyticsConsentChange);
  consentListenerInitialized = true;
  handleAnalyticsConsentChange();
}

export function initPAPClient() {
  if (isPAPAnalyticsConsentGateEnabled()) {
    return undefined;
  }

  amplitude.getInstance().init(AMPLITUDE_KEY, undefined, {
    apiEndpoint: API_ENDPOINT,
  });

  return amplitude;
}

function initPAPClientV2() {
  const papClient = amplitude.getInstance() as PAPAmplitudeClient;
  const disableCookies = !hasAnalyticsConsent();

  if (
    !papClientInitialized ||
    papClient.options.disableCookies !== disableCookies
  ) {
    papClient.init(AMPLITUDE_KEY, undefined, {
      apiEndpoint: API_ENDPOINT,
      disableCookies,
    });
    papClientInitialized = true;
  }

  return papClient;
}

export const logPAPEvent = (
  event: PAPEvent,
  userId: AmplitudeUserId = undefined,
  foreignUserId: AmplitudeUserId = undefined,
  userProperties: object = {},
) => {
  if (!isPAPAnalyticsConsentGateEnabled()) {
    const papUser = getPAPUser();
    const resolvedUserId = userId !== undefined ? userId : papUser?.dbxUserId;
    const resolvedForeignUserId =
      foreignUserId !== undefined ? foreignUserId : papUser?.guid;

    return initPAPClient()
      ?.getInstance()
      .logEvent(`${event.action}.${event.object}`, {
        user_properties: userProperties,
        event_action: event.action,
        event_object: event.object,
        event_class: event.class,
        user_identifier: {
          user_id: resolvedUserId,
          foreign_user_id: resolvedForeignUserId,
          // Dropbox is expecting a literal HELLOSIGN value for this API request
          // eslint-disable-next-line no-restricted-syntax
          foreign_user_provider: 'HELLOSIGN',
        },
        ...(event.properties ? snakeCaseObj(event.properties) : {}),
      });
  }

  initializeAnalyticsConsentHandling();

  const papClient = initPAPClientV2();
  const papUser = getPAPUser();
  const resolvedUserId = userId !== undefined ? userId : papUser?.dbxUserId;
  const resolvedForeignUserId =
    foreignUserId !== undefined ? foreignUserId : papUser?.guid;

  return papClient.logEvent(`${event.action}.${event.object}`, {
    user_properties: userProperties,
    event_action: event.action,
    event_object: event.object,
    event_class: event.class,
    user_identifier: {
      user_id: resolvedUserId,
      foreign_user_id: resolvedForeignUserId,
      // Dropbox is expecting a literal HELLOSIGN value for this API request
      // eslint-disable-next-line no-restricted-syntax
      foreign_user_provider: 'HELLOSIGN',
    },
    ...(event.properties ? snakeCaseObj(event.properties) : {}),
  });
};

export const integrationNames = {
  DeepIntegration: 'deepintegration',
  OfficeSelfSign: 'officeAddInSelfSign',
  HubSpot: 'hubspot',
  HubSpotV2: 'hubspotv2',
} as const;
export type IntegrationNames =
  (typeof integrationNames)[keyof typeof integrationNames];

export function retrieveFormattedIntegrationSource(
  integrationName: string,
): IntegrationSource {
  switch (integrationName) {
    case integrationNames.OfficeSelfSign:
      return 'office_add_in';
    case integrationNames.DeepIntegration:
      return 'deep_integration';
    case integrationNames.HubSpot:
      return 'hubspot';
    case integrationNames.HubSpotV2:
      return 'hubspotv2';
    default:
      return 'standalone';
  }
}
